import type { ReportDocumentProps } from '../pdf/ReportDocument';

interface PdfExportOptions {
  /** Filename without extension. Sanitized for the OS. */
  filename: string;
}

function sanitizeFilename(name: string): string {
  return name.replace(/[\\/?%*:|"<>]/g, '_').replace(/\s+/g, '_').slice(0, 120);
}

/**
 * Generate a PDF from the diagnostic report data using @react-pdf/renderer.
 *
 * @react-pdf/renderer is large (>2MB) and pre-bundling it eagerly via static
 * imports causes Vite's dep optimizer to time out, which breaks the entire
 * report page (504 Gateway Timeout when loading the chunk). We use dynamic
 * imports so the bundle is fetched only when the user actually clicks
 * "PDFで出力" — the report page itself never loads it.
 */
export async function exportReportToPdf(
  data: ReportDocumentProps,
  options: PdfExportOptions,
): Promise<void> {
  const [{ pdf }, { ReportDocument }] = await Promise.all([
    import('@react-pdf/renderer'),
    import('../pdf/ReportDocument'),
  ]);

  const blob = await pdf(<ReportDocument {...data} />).toBlob();

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${sanitizeFilename(options.filename)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
